import { Alert, Button, Checkbox, Group, Modal, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { schemaResolver, useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { z } from "zod";

import { errorMessage, isApiError, type User } from "@/api";
import { confirmAction } from "@/components";
import { ROLE_LABELS, STAFF_ROLES, type StaffRole } from "@/shell/roles";
import { useMe } from "@/shell/session";

import { type StaffPatch, useCreateStaffMutation, useUpdateStaffMutation } from "./api";

const schema = z.object({
  email: z.email("Enter a valid email"),
  display_name: z.string().trim().max(120),
  roles: z.array(z.enum(STAFF_ROLES)).min(1, "Pick at least one role"),
  status: z.enum(["ACTIVE", "BLOCKED"]),
});
type Values = z.infer<typeof schema>;

const isStaffRole = (r: string): r is StaffRole => (STAFF_ROLES as readonly string[]).includes(r);

type Props = {
  /** null = closed, "new" = create, a user = edit. */
  target: User | "new" | null;
  onClose: () => void;
};

/** Create / edit a staff user. Self: status control hidden (no self-block); dropping own superadmin is confirmed. */
export function StaffFormModal({ target, onClose }: Props) {
  const me = useMe();
  const editing = target !== null && target !== "new" ? target : null;
  const isSelf = editing?.public_id !== undefined && editing.public_id === me?.public_id;
  const create = useCreateStaffMutation();
  const update = useUpdateStaffMutation();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<Values>({
    initialValues: { email: "", display_name: "", roles: [], status: "ACTIVE" },
    validate: schemaResolver(schema, { sync: true }),
  });

  useEffect(() => {
    setFormError(null);
    form.setValues({
      email: editing?.email ?? "",
      display_name: editing?.display_name ?? "",
      roles: (editing?.roles ?? []).filter(isStaffRole),
      status: editing?.status === "BLOCKED" ? "BLOCKED" : "ACTIVE",
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when the target changes
  }, [target]);

  const submit = async (v: Values) => {
    setFormError(null);
    try {
      if (!editing) {
        await create.mutateAsync({ email: v.email.trim(), display_name: v.display_name, roles: v.roles });
        notifications.show({ color: "green", message: `Invitation sent to ${v.email}.` });
      } else {
        const patch: StaffPatch = {};
        if (form.isDirty("display_name")) patch.display_name = v.display_name;
        if (form.isDirty("roles")) patch.roles = v.roles;
        if (form.isDirty("status") && !isSelf) patch.status = v.status;
        if (patch.status === "BLOCKED") {
          const ok = await confirmAction({
            title: "Block this user?",
            consequence: `${editing.email} will be signed out and unable to sign in until reactivated.`,
            confirmLabel: "Block",
          });
          if (!ok) return;
        }
        if (isSelf && patch.roles && !patch.roles.includes("superadmin") && editing.roles?.includes("superadmin")) {
          const ok = await confirmAction({
            title: "Remove your own superadmin role?",
            consequence: "You will lose access to Staff and every superadmin-only screen immediately.",
            confirmLabel: "Remove",
          });
          if (!ok) return;
        }
        if (Object.keys(patch).length) await update.mutateAsync({ id: editing.public_id ?? "", patch });
        notifications.show({ color: "green", message: "Saved." });
      }
      onClose();
    } catch (e) {
      if (isApiError(e, "EMAIL_TAKEN")) form.setFieldError("email", "A user with this email already exists");
      else if (isApiError(e, "LAST_SUPERADMIN"))
        setFormError(
          "This is the last active superadmin: it cannot be demoted or blocked. Make someone else superadmin first.",
        );
      else setFormError(errorMessage(e));
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Modal opened={target !== null} onClose={onClose} title={editing ? "Edit staff user" : "New staff user"} centered>
      <form onSubmit={form.onSubmit(submit)} data-testid="staff-form">
        <Stack>
          <TextInput
            label="Email"
            withAsterisk
            disabled={Boolean(editing)}
            {...form.getInputProps("email")}
            data-testid="staff-email"
          />
          <TextInput label="Display name" {...form.getInputProps("display_name")} data-testid="staff-name" />
          <Checkbox.Group label="Roles" withAsterisk {...form.getInputProps("roles")}>
            <Stack gap={6} mt={6}>
              {STAFF_ROLES.map((r) => (
                <Checkbox key={r} value={r} label={ROLE_LABELS[r]} data-testid={`role-${r}`} />
              ))}
            </Stack>
          </Checkbox.Group>
          {editing && !isSelf && (
            <div>
              <Text size="sm" fw={500} mb={4}>
                Status
              </Text>
              <SegmentedControl
                data={[
                  { label: "Active", value: "ACTIVE" },
                  { label: "Blocked", value: "BLOCKED" },
                ]}
                {...form.getInputProps("status")}
                data-testid="staff-status"
              />
            </div>
          )}
          {!editing && (
            <Text size="xs" c="dimmed">
              The user receives an email link to set a password, then enrols 2FA.
            </Text>
          )}
          {formError && (
            <Alert color="red" data-testid="form-error">
              {formError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} data-testid="staff-submit">
              {editing ? "Save" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
